import * as React from 'react';

import { cn } from '@rematter/ui-web';
import { useAtom } from 'jotai';
import {
  LucideCamera,
  LucideCpu,
  LucideMonitorSmartphone,
  LucidePrinter,
  LucideScanLine,
  LucideWeight,
} from 'lucide-react';

import * as state from '../../_state/hardware.state';
import { useTranslations } from '../../i18n/useTranslations';
import { HardwareSection } from '../../types/sidecar';

import { CamerasListView } from './cameras/_CamerasListView';
import { PrintersListView } from './printers/_PrintersListView';
import { ScalesListView } from './scales/_ScalesListView';
import { ScannersListView } from './scanners/_ScannersListView';
import { SidecarsListView } from './sidecars/_SidecarsListView';
import { UStudioView } from './ustudio/_UStudioView';

const SECTION_IDS: { id: HardwareSection; icon: React.ElementType }[] = [
  { id: 'sidecars', icon: LucideCpu },
  { id: 'cameras', icon: LucideCamera },
  { id: 'scanners', icon: LucideScanLine },
  { id: 'printers', icon: LucidePrinter },
  { id: 'scales', icon: LucideWeight },
  { id: 'ustudio', icon: LucideMonitorSmartphone },
];

export const HardwareManagementView = () => {
  const t = useTranslations();
  const [currentSection, setCurrentSection] = useAtom(state.$currentSection);

  const sectionLabels: Record<HardwareSection, string> = {
    sidecars: t.hardwareView.sections.sidecars,
    cameras: t.hardwareView.sections.cameras,
    scanners: t.hardwareView.sections.scanners,
    printers: t.hardwareView.sections.printers,
    scales: t.hardwareView.sections.scales,
    ustudio: t.hardwareView.sections.ustudio,
  };

  const activeSection = (currentSection as HardwareSection) || 'sidecars';

  React.useEffect(() => {
    if (!currentSection) {
      setCurrentSection('sidecars' as unknown as HardwareSection);
    }
  }, [currentSection, setCurrentSection]);

  const renderPanel = () => {
    switch (activeSection) {
      case 'sidecars':
        return <SidecarsListView />;
      case 'cameras':
        return <CamerasListView />;
      case 'scanners':
        return <ScannersListView />;
      case 'printers':
        return <PrintersListView />;
      case 'scales':
        return <ScalesListView />;
      case 'ustudio':
        return <UStudioView />;
      default:
        return <SidecarsListView />;
    }
  };

  return (
    <div className="@HardwareManagementView flex h-full">
      <div className="w-[200px] shrink-0 border-r border-solid border-neutral/10 bg-white">
        <div className="border-b border-solid border-neutral/10 px-3 py-2 text-xs font-medium text-neutral/40">
          {t.hardwareView.sidebar.title}
        </div>
        {SECTION_IDS.map((section) => {
          const isSelected = section.id === activeSection;
          const Icon = section.icon;

          return (
            <button
              key={section.id}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm outline-none hover:bg-neutral/10',
                isSelected && 'bg-neutral/60 text-white hover:bg-neutral/60',
              )}
              onClick={(e) => {
                e.preventDefault();
                setCurrentSection(section.id as unknown as HardwareSection);
              }}
            >
              <Icon className="size-4" />
              <span>{sectionLabels[section.id]}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-1 flex-col overflow-hidden">
        {renderPanel()}
      </div>
    </div>
  );
};
